import {Component,OnDestroy,OnInit} from "@angular/core";
import {TranslateService} from "@ngx-translate/core";
import {PageHeaderItem} from "@share/component/page-header/page-header";
import {Store} from "@ngrx/store";
import {AppState} from "@domain/appstate";
import {Session} from "@domain/security/session";
import {ActivatedRoute,NavigationEnd,Router} from "@angular/router";
import {isRouteAllowed} from "@core/security/role-admin-helpers";
import {BehaviorSubject,Observable,startWith} from "rxjs";
import {FloatingButtonAction} from "@share/component/floating-button/floating-button";
import {filterFirstNotNull} from "@share/utils/rxjs-custom-operator";
import {IComponentWithRoutedTabs} from "@domain/admin/recherche/component-with-routed-tabs";
import {Subscription} from "rxjs";
import {distinctUntilChanged,filter,map,switchMap} from "rxjs/operators";

/**
 * Enum pour les noms des différents onglets de la page API
 */
export enum Onglets {
    REST = "Rest",
    SOAP = "Soap",
    CLEFS = "Clefs",
    PARTENAIRES = "Partenaires"
}

/**
 * Sous-menu API
 *
 * @author Guillaume TRAVOT
 * @date 21/11/2023
 */
@Component({
    host: {'data-test-id': 'parametre-api'},
    templateUrl: './parametre-api.component.html'
})
export class ParametreApiComponent implements OnInit, OnDestroy, IComponentWithRoutedTabs {
    /** Liste statique des onglets */
    public static listeOnglets: Array<PageHeaderItem> = [
        {
            code: Onglets.REST,
            libelle: 'admin.parametres.api.onglets.rest',
            url: 'Admin/Parametre/API/Rest'
        }, {
            code: Onglets.SOAP,
            libelle: 'admin.parametres.api.onglets.soap',
            url: 'Admin/Parametre/API/Soap'
        }, {
            code: Onglets.CLEFS,
            libelle: 'admin.parametres.api.onglets.clefs',
            url: 'Admin/Parametre/API/Clefs'
        }, {
            code: Onglets.PARTENAIRES,
            libelle: 'admin.parametres.api.onglets.partenaires',
            url: 'Admin/Parametre/API/Partenaires'
        }
    ];

    /** Liste des actions possibles */
	listeActions: BehaviorSubject<Array<FloatingButtonAction>> = new BehaviorSubject<Array<FloatingButtonAction>>(null);

    /** Liste des différents onglets disponibles dans le menu paramètres */
    listeTabItems: Array<PageHeaderItem> = ParametreApiComponent.listeOnglets;

    /** Onglet courant */
    selectedItem: PageHeaderItem;

    /** Référence vers l'enum des onglets entreprise pour l'utiliser dans le template */
    Onglet = Onglets;

    /** Index de l'onglet courant */
    selectedIndex: number;

    /** Observable indiquant si le header doit être affiché */
    showHeader$: Observable<boolean>;

    /** Session */
    private session: Session;

    /** Souscription */
    private subscription: Subscription;

    constructor(
        protected translateService: TranslateService,
        private router: Router,
        private store: Store<AppState>,
        private activatedRoute: ActivatedRoute,
    ) { }

    /**
     * Initialisation du composant
     */
    ngOnInit(): void {
        //Récupération de la session
        this.store.select(state => state.session).pipe(filterFirstNotNull()).subscribe((session: Session) => {
            //Stockage de la session
            this.session = session;

            //Filtrage des onglets autorisés
            this.listeTabItems = this.listeTabItems.filter(i => isRouteAllowed(this.router, this.session, i.url));

            this.subscription = this.activatedRoute.url.subscribe(() => {
                //Récupération de la route
                const firstChild = this.activatedRoute.snapshot.firstChild;

                //Extraction du path
                const path = firstChild.url[0].path;

                //On vérifie si on trouve l'onglet demandé via l'url
                const matching = this.listeTabItems.find(i => i.code === path);

                //Si on trouve bien l'onglet correspondant
                if (!!matching) {
                    //on met à jour le composant header
                    setTimeout(() => {
                        this.selectedItem = matching;
                        this.selectedIndex = this.listeTabItems.findIndex(i => i.code === path);
                    });
                } else {
                    //Sinon, on redirige sur le premier onglet et on met à jour le composant header
                    setTimeout(() => {
                        this.selectedItem = this.listeTabItems[0];
                        this.selectedIndex = 0;
                    });
                }
            });
        });

        //Abonnement aux évènements du router
        this.showHeader$ = this.router.events.pipe(
            //Filtre les évènements NavigationEnd seuls
            filter(e => e instanceof NavigationEnd),
            //Récupération de l'activatedRoute
            map(() => this.activatedRoute),
            //Récupération de la route
            map(route => {
                while (route.firstChild) route = route.firstChild;
                return route;
            }),
            //Récupération des données de la route
            switchMap(route => route.data),
            //Si hideHeader est présent et à true
            map(data => !data['hideHeader']),
            //Initialisation de l'observable avec la valeur de la route actuelle
            startWith(
                (() => {
                    let r: ActivatedRoute | null = this.activatedRoute;
                    while (r?.firstChild) r = r.firstChild;
                    return !(r?.snapshot.data?.['hideHeader']);
                })()
            ),
            //Emission seulement si différent de la valeur précédente
            distinctUntilChanged()
        );
    }

    /**
     * Changement d'onglet
     *
     * @param selectedItem Onglet sélectionné
     */
    onSelectedItemChange(selectedItem: PageHeaderItem): void {
        if (!this.selectedItem) {
            this.selectedItem = selectedItem;
        }
        if (selectedItem?.code !== this.selectedItem?.code) {
            this.selectedItem = selectedItem;
            this.router.navigateByUrl(selectedItem.url);
        }
    }

    /**
     * Destruction du composant
     */
    ngOnDestroy() {
        //Résiliation de l'abonnement
        this.subscription.unsubscribe();
    }
}